/**
 * Implementation extended from:
 * https://www.contentful.com/developers/docs/tutorials/general/getting-started-with-rich-text-field-type/
 * https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer
 */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Block, Document, Hyperlink, Text } from '@contentful/rich-text-types';
import { isExternalLink } from '@coursera-web/shared-articles';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import AccordionFaq from 'bundles/premium-hub/components/degrees/redesign/AccordionFaq';
import type { AdmissionsLinkFragment } from 'bundles/xddp/__generated__/contentfulTypes';

const xddpRichTextEmbedTypes = {
  faq: 'Faq',
} as const;

type Props = {
  content: Document;
  links?: AdmissionsLinkFragment;
  getRichTextOptions?: (links?: AdmissionsLinkFragment) => Options;
};

export const getDefaultRichTextOptions = (links?: AdmissionsLinkFragment): Options => {
  let faqCounter = 0;

  return {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const blockNode = node as Block;
        const id = blockNode?.data?.target?.sys?.id;
        const entry = links?.entries?.block?.find((link) => link?.sys?.id === id);

        const isFirstFAQ = faqCounter === 0;

        switch (entry?.__typename) {
          case xddpRichTextEmbedTypes.faq:
            if (!entry.question || !entry.answer) {
              return null;
            }
            faqCounter += 1;
            return (
              <AccordionFaq
                key={`faq-${id}`}
                question={entry.question}
                answer={entry.answer}
                defaultExpanded={isFirstFAQ}
              />
            );
          default:
            return null;
        }
      },

      [INLINES.HYPERLINK]: (node) => {
        const inlineNode = node as Hyperlink;
        const isBold = inlineNode?.content[0]?.marks[0]?.type === 'bold';
        return (
          <TrackedLink2
            href={node.data.uri}
            target={isExternalLink(node.data.uri) ? '_blank' : '_self'}
            rel={isExternalLink(node.data.uri) ? 'noopener noreferrer' : undefined}
            trackingName="ddp_app_process_link"
            css={css`
              font-weight: ${isBold ? 'bold' : 'inherit'};
              text-decoration: underline;
            `}
          >
            {(node.content[0] as Text).value}
          </TrackedLink2>
        );
      },
    },
  };
};

const RichText = ({ content, links, getRichTextOptions = getDefaultRichTextOptions }: Props) => {
  return <div className="rc-RichText">{documentToReactComponents(content, getRichTextOptions(links))}</div>;
};

export default RichText;
